import { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import { seoArticles, type ArticleData } from '@/mocks/articles';
import { categories } from '@/mocks/categories';

function ArticleNotFound() {
  return (
    <div className="text-center py-20">
      <div className="w-16 h-16 rounded-full bg-accent-100 flex items-center justify-center mx-auto mb-6">
        <i className="ri-file-search-line text-accent-600 text-2xl"></i>
      </div>
      <h2 className="text-2xl font-bold text-foreground-900 font-heading mb-3">Artykuł nie znaleziony</h2>
      <p className="text-foreground-600 mb-8">Przepraszamy, ale artykuł o podanym adresie nie istnieje.</p>
      <Link
        to="/"
        className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
      >
        <i className="ri-home-4-line"></i>
        Wróć na stronę główną
      </Link>
    </div>
  );
}

function ArticleContent({ article }: { article: ArticleData }) {
  return (
    <article className="w-full max-w-4xl mx-auto px-6 md:px-10 py-10 md:py-16">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-foreground-500 mb-8 flex-wrap" aria-label="Breadcrumb">
        <Link to="/" className="hover:text-accent-600 transition-colors cursor-pointer whitespace-nowrap">Strona główna</Link>
        <i className="ri-arrow-right-s-line text-xs"></i>
        <span className="text-foreground-800 font-medium whitespace-nowrap">{article.title}</span>
      </nav>

      {/* Header */}
      <header className="mb-10">
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <span className="px-3 py-1 rounded-full bg-accent-100 text-accent-800 text-xs font-semibold tracking-wider uppercase whitespace-nowrap">{article.category}</span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.date}</span>
          <span className="text-xs text-foreground-400">·</span>
          <span className="text-xs text-foreground-400 whitespace-nowrap">{article.readTime} min czytania</span>
        </div>
        <h1 className="text-3xl md:text-5xl lg:text-6xl font-bold text-foreground-900 font-heading leading-tight">
          {article.title}
        </h1>
        <p className="text-base md:text-lg text-foreground-600 mt-5 leading-relaxed max-w-3xl">
          {article.intro}
        </p>
      </header>

      {/* Featured Image */}
      <div className="mb-12 rounded-2xl overflow-hidden">
        <img
          src={article.imageUrl}
          alt={article.title}
          className="w-full h-auto"
        />
      </div>

      {/* Content Sections */}
      {article.sections.map((section, idx) => (
        <section key={idx} className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-5">
            {section.heading}
          </h2>
          {section.paragraphs.map((para, pIdx) => (
            <p key={pIdx} className="text-base text-foreground-700 leading-relaxed mb-4">
              {para}
            </p>
          ))}
        </section>
      ))}

      {/* FAQ */}
      {article.faq && article.faq.length > 0 && (
        <section className="mb-12">
          <h2 className="text-2xl md:text-3xl font-bold text-foreground-900 font-heading mb-6">
            Często zadawane pytania (FAQ)
          </h2>
          <div className="space-y-3">
            {article.faq.map((item, i) => (
              <details key={i} className="group rounded-xl bg-background-100 border border-background-200/60 overflow-hidden transition-all duration-200">
                <summary className="flex items-center justify-between px-5 py-4 cursor-pointer hover:bg-background-200/40 transition-colors">
                  <span className="text-sm md:text-base font-semibold text-foreground-900 pr-4">{item.q}</span>
                  <span className="w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-200 group-open:rotate-180">
                    <i className="ri-arrow-down-s-line text-foreground-500"></i>
                  </span>
                </summary>
                <div className="px-5 pb-4">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.a}</p>
                </div>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="mb-12 bg-primary-500 rounded-2xl p-8 md:p-12 text-center">
        <h2 className="text-2xl md:text-3xl font-bold text-background-50 font-heading mb-4">
          Znajdź najlepsze produkty już teraz
        </h2>
        <p className="text-sm md:text-base text-background-50/80 mb-8 max-w-xl mx-auto leading-relaxed">
          Skorzystaj z gotowych, uporządkowanych list produktów i odkryj najpopularniejsze Kakobuy Finds w kilka sekund.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-background-50 text-primary-600 font-semibold text-base hover:bg-background-100 transition-all duration-300 hover:scale-105 whitespace-nowrap cursor-pointer shadow-lg"
          >
            <i className="ri-shopping-bag-3-line"></i>
            Kakobuy Spreadsheet
          </a>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-8 py-3.5 rounded-full bg-primary-600 text-background-50 font-semibold text-base border border-background-50/30 hover:bg-primary-700 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-home-4-line"></i>
            Strona główna
          </Link>
        </div>
      </section>

      {/* Internal Links */}
      <div className="border-t border-background-200/60 pt-8">
        <h4 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Przydatne linki</h4>
        <div className="flex flex-wrap gap-3">
          <Link to="/" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
            Strona główna KakobuyFinds
          </Link>
          <Link to="/kakobuy-spreadsheet" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
            Kakobuy Spreadsheet – poradnik
          </Link>
          <Link to="/poradnik-zakupowy" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
            Poradnik zakupowy
          </Link>
          {categories.slice(0, 5).map((cat) => (
            <a key={cat.id} href={cat.url} target="_blank" rel="noopener nofollow" className="text-sm text-accent-600 hover:text-accent-700 transition-colors cursor-pointer whitespace-nowrap">
              {cat.name}
            </a>
          ))}
        </div>
      </div>

      {/* Related Articles */}
      <div className="border-t border-background-200/60 pt-8 mt-8">
        <h4 className="text-sm font-semibold text-foreground-500 uppercase tracking-wider mb-4">Powiązane artykuły</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {seoArticles
            .filter((a) => a.slug !== article.slug)
            .slice(0, 3)
            .map((related) => (
              <Link
                key={related.slug}
                to={`/artykul/${related.slug}`}
                className="p-4 rounded-xl bg-background-100 border border-background-200/60 hover:border-accent-200 transition-all duration-200 cursor-pointer group"
              >
                <p className="text-sm font-semibold text-foreground-900 group-hover:text-accent-700 transition-colors line-clamp-2">
                  {related.title}
                </p>
                <p className="text-xs text-foreground-500 mt-2 whitespace-nowrap">{related.readTime} min czytania</p>
              </Link>
            ))}
        </div>
      </div>

      {/* Structured Data */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "Article",
        "headline": article.title,
        "description": article.metaDescription,
        "datePublished": article.date,
        "dateModified": article.date,
        "author": { "@type": "Organization", "name": "KakobuyFinds" },
        "inLanguage": "pl-PL",
        "mainEntityOfPage": { "@type": "WebPage", "@id": `https://kakobuyfinds.autos/artykul/${article.slug}` }
      }) }}></script>

      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Strona główna", "item": "https://kakobuyfinds.autos/" },
          { "@type": "ListItem", "position": 2, "name": article.title, "item": `https://kakobuyfinds.autos/artykul/${article.slug}` }
        ]
      }) }}></script>
    </article>
  );
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = seoArticles.find((a) => a.slug === slug);

  return (
    <>
      <Navbar />
      <main className="pt-20">
        {article ? <ArticleContent article={article} /> : <ArticleNotFound />}
      </main>
      <Footer />
    </>
  );
}